import { type MouseEvent, type SyntheticEvent, useMemo, useState } from "react";

import i18next, { t } from "i18next";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {
  Box,
  Button,
  Chip,
  Popover,
  Stack,
  SxProps,
  TextField,
  Theme
} from "@mui/material";

import {
  DateRangeFilterValue,
  DateRangePresetSet,
  buildPresetValue,
  formatRangeLabel,
  getPresetsForSet,
  isDateRangeEmpty,
  isDateRangeFilterValue
} from "@/src/components/organisms/AdminDataTable/dateRangeFilter";
import { Column } from "@/src/components/organisms/AdminDataTable/reactTable";

const triggerSx: SxProps<Theme> = (theme) => ({
  width: "100%",
  justifyContent: "space-between",
  textTransform: "none",
  backgroundColor: theme.palette.background.paper,
  borderColor: theme.palette.misc.outlineBorder,
  color: theme.palette.text.primary,
  fontWeight: theme.typography.fontWeightRegular,
  px: 1,
  minHeight: 40,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.text.secondary
  }
});

const popoverContentSx: SxProps<Theme> = {
  p: 2,
  width: 320,
  display: "flex",
  flexDirection: "column",
  gap: 1.5
};

const presetRowSx: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  gap: 0.75
};

interface DateRangeFilterInputProps<TData extends object> {
  column: Column<TData, unknown>;
  presetSet?: DateRangePresetSet;
}

const readInitialValue = (raw: unknown): DateRangeFilterValue | undefined => {
  if (!isDateRangeFilterValue(raw)) return undefined;
  if (isDateRangeEmpty(raw)) return undefined;
  return raw;
};

const DateRangeFilterInput = <TData extends object>(
  props: DateRangeFilterInputProps<TData>
) => {
  const { column, presetSet = "past" } = props;
  const presets = useMemo(() => getPresetsForSet(presetSet), [presetSet]);

  const initial = readInitialValue(column.getFilterValue());
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draftFrom, setDraftFrom] = useState<string>(initial?.from ?? "");
  const [draftTo, setDraftTo] = useState<string>(initial?.to ?? "");
  const [draftPresetId, setDraftPresetId] = useState<string | undefined>(
    initial?.presetId
  );

  const isOpen = Boolean(anchorEl);
  const triggerLabel = formatRangeLabel(initial, presets);
  const hasActiveRange = !isDateRangeEmpty(initial);

  const openPopover = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setDraftFrom(initial?.from ?? "");
    setDraftTo(initial?.to ?? "");
    setDraftPresetId(initial?.presetId);
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => setAnchorEl(null);

  const handlePresetClick = (presetId: string) => {
    const preset = presets.find((p) => p.id === presetId);
    if (!preset) return;
    const value = buildPresetValue(preset);
    setDraftFrom(value.from ?? "");
    setDraftTo(value.to ?? "");
    setDraftPresetId(value.presetId);
  };

  const handleFromChange = (value: string) => {
    setDraftFrom(value);
    setDraftPresetId(undefined);
  };

  const handleToChange = (value: string) => {
    setDraftTo(value);
    setDraftPresetId(undefined);
  };

  const handleApply = () => {
    if (!draftFrom && !draftTo) {
      column.setFilterValue(undefined);
    } else {
      const next: DateRangeFilterValue = {
        from: draftFrom || undefined,
        to: draftTo || undefined,
        presetId: draftPresetId
      };
      column.setFilterValue(next);
    }
    closePopover();
  };

  const handleClear = () => {
    setDraftFrom("");
    setDraftTo("");
    setDraftPresetId(undefined);
    column.setFilterValue(undefined);
    closePopover();
  };

  const stopPropagation = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  return (
    <Box onClick={stopPropagation} onMouseDown={stopPropagation}>
      <Button
        variant="outlined"
        size="small"
        color={hasActiveRange ? "primary" : "inherit"}
        onClick={openPopover}
        startIcon={<CalendarMonthIcon fontSize="small" />}
        sx={triggerSx}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <Box
          component="span"
          sx={{
            flex: 1,
            textAlign: "left",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {triggerLabel}
        </Box>
      </Button>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { onClick: stopPropagation } }}
      >
        <Box sx={popoverContentSx}>
          <Box sx={presetRowSx}>
            {presets.map((preset) => (
              <Chip
                key={preset.id}
                label={preset.label}
                size="small"
                color={draftPresetId === preset.id ? "primary" : "default"}
                variant={draftPresetId === preset.id ? "filled" : "outlined"}
                onClick={() => handlePresetClick(preset.id)}
              />
            ))}
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField
              type="date"
              size="small"
              fullWidth
              label={t("admin.dataTable.filters.dateFrom")}
              value={draftFrom}
              onChange={(e) => handleFromChange(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              type="date"
              size="small"
              fullWidth
              label={t("admin.dataTable.filters.dateTo")}
              value={draftTo}
              onChange={(e) => handleToChange(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{ justifyContent: "flex-end" }}
          >
            <Button size="small" onClick={handleClear}>
              {t("admin.dataTable.filters.clear")}
            </Button>
            <Button size="small" variant="contained" onClick={handleApply}>
              {t("admin.dataTable.filters.apply")}
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

export default DateRangeFilterInput;
